import { defineStore } from "pinia";
import type { WorkspaceDetails } from "@/_store/workspaces-list.module.ts";
import {
  defaultSubscriptionModulesConfig,
  SubscriptionAddon,
  SubscriptionModule,
  WorkspaceLocale,
  type WorkspaceType,
  CoroBanner,
} from "@/constants/workplaces.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import api from "@/_helpers/api.ts";
import { setDefaultModules } from "@/_helpers/subscription";
import { axiosInstance } from "@/plugins/https.ts";
import cloneDeep from "lodash/cloneDeep";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";

import { useTutorialStore } from "@/_store/tutorial.module.ts";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import type { AxiosResponse } from "axios";
import type { Subscription } from "@/_store/msp/msp.module.ts";
import { StoreModule } from "@/constants/store.ts";
import { isCanadaEnv } from "@/constants/general.ts";
import unset from "lodash/unset";
export const appLogoDefaultPath = "/images/logos/coro-logo-small.svg";

export interface CurrentWorkspaceDetails extends WorkspaceDetails {
  trialAvailable?: boolean;
}

export type ModulesSettings = {
  [key in SubscriptionModule]: SubscriptionModuleInfo;
};

export interface SubscriptionModuleInfo {
  enabled: boolean;
  addons: SubscriptionAddonInfo[];
}

export interface SubscriptionAddonInfo {
  enabled: boolean;
  name: SubscriptionAddon;
}

export interface WorkspaceStoreState {
  brandingAlias: string;
  workspace: CurrentWorkspaceDetails;
  appLogo: string;
  workspaceAndPermissionsUpdatedTimestamp: number;
  hiddenBanners: CoroBanner[];
}

export const defaultWorkspaceState: WorkspaceStoreState = {
  workspace: {
    workspaceId: "",
    name: "",
    domain: "",
    type: "" as WorkspaceType,
    mfaRequired: false,
    scanUnprotectedUsers: false,
    supportEnabled: false,
    branding: {
      headerIconUrl: "",
      supportEmail: "",
      displayName: "",
      brandColor: "",
    },
    isCoronetWorkspace: false,
    subscription: {
      modules: {
        ...cloneDeep(defaultSubscriptionModulesConfig),
      },
      bundles: [],
    } as Subscription,
    demoModeEndDate: null,
    demoModeEnabled: false,
    languageCode: WorkspaceLocale.EN_US,
    subscriptionType: "",
    trialEndDate: 0,
    headerIconUrl: "",
    showDisabledModules: false,
    betaComponents: [],
    parentWorkspaceName: "",
    trialModeStartedNotice: false,
    allowPolicyOverriding: true,
    mspPolicyShortInfo: {
      mspPolicyId: "",
      workspaceId: "",
      name: "",
      sections: [],
    },
  },
  appLogo: appLogoDefaultPath,
  workspaceAndPermissionsUpdatedTimestamp: 0,
  brandingAlias: "",
  hiddenBanners: [],
};

export const useWorkspaceStore = defineStore(StoreModule.WORKSPACE, {
  persist: true,
  getters: {},
  state: (): WorkspaceStoreState => cloneDeep(defaultWorkspaceState),
  actions: {
    async requestBrandingInfo(alias: string) {
      const accountStore = useAccountStore();
      const request = {
        ...api.getBranding(alias),
      };
      try {
        const { data } = await axiosInstance.request(request);
        this.brandingAlias = alias;
        this.setAppLogo(data.headerIconUrl);
        this.workspace.branding.brandColor = data.brandColor ?? "";
      } catch {
        this.brandingAlias = "";
        if (!accountStore.logged) {
          this.setAppLogo(appLogoDefaultPath);
          this.workspace.branding.brandColor = "";
        }
      }
    },
    async updateWorkspaceAndPermissions(workspaceId: string, isMsp: boolean) {
      useAppsManagementModule().handleAppsManagement(workspaceId, isMsp);
      this.workspace.workspaceId = workspaceId;
      await this.getCurrentWorkspace();
      this.workspaceAndPermissionsUpdatedTimestamp = new Date().getTime();
    },
    async getCurrentWorkspace() {
      const request = {
        ...api.getCurrentWorkspace,
      };

      try {
        // Create an array of promises because we need to execute this tho together
        const promises: [Promise<AxiosResponse<WorkspaceDetails>>, Promise<void>] = [
          axiosInstance.request(request), // Fetch workspace data
          usePermissionsStore().getUserPermissions(this.workspace.workspaceId), // Fetch workspace permissions
        ];
        const [workspaceResponse] = await Promise.all(promises);

        const { data } = workspaceResponse;
        this.setWorkspace(data);
      } catch (e) {
        console.error(e);
      }
    },
    async requestTrialForCurrentWorkspace() {
      const request = {
        ...api.requestTrialForCurrentWorkspace(),
      };
      await axiosInstance.request(request);
    },
    setWorkspace(workplace: WorkspaceDetails) {
      const modules = setDefaultModules(cloneDeep(workplace.subscription.modules));
      // TODO: Remove crutch once SAT will be available in Canada
      if (isCanadaEnv) {
        unset(modules, SubscriptionModule.SECURITY_AWARENESS_TRAINING);
      }
      this.workspace = cloneDeep({
        ...workplace,
        languageCode: workplace.languageCode ?? WorkspaceLocale.EN_US,
        subscription: {
          modules,
          bundles: cloneDeep(workplace.subscription.bundles ?? []),
        },
      });
      this.setAppLogo(
        workplace.headerIconUrl || (workplace.branding && workplace.branding.headerIconUrl)
      );
      useTutorialStore().$patch({ showTrialModeStartedNotice: workplace.trialModeStartedNotice });
    },
    setAppLogo(logo?: string) {
      if (!logo) {
        this.appLogo = appLogoDefaultPath;
        return;
      }
      if (
        (logo.startsWith("http://") || logo.startsWith("https://")) &&
        !logo.includes("X-Amz-Signature")
      ) {
        const url = new URL(logo);
        url.searchParams.set("v", String(Date.now()));
        this.appLogo = url.toString();
      } else {
        this.appLogo = logo;
      }
    },
    addHiddenBanner(banner: CoroBanner) {
      this.hiddenBanners.push(banner);
    },
    resetStateWithoutBranding() {
      const brandingAlias = this.brandingAlias;
      const appLogo = this.appLogo;
      const brandColor =
        this.workspace.branding?.brandColor ?? defaultWorkspaceState.workspace.branding.brandColor;

      this.$patch((state) => {
        Object.assign(state, cloneDeep(defaultWorkspaceState));
        state.brandingAlias = brandingAlias;
        state.appLogo = appLogo;
        state.workspace.branding.brandColor = brandColor;
      });
    },
  },
});
